'use client';

import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';

type LocaleKey = 'ro' | 'en' | 'bg' | 'el' | 'hu' | 'ru';

const SUPPORTED_LOCALES = new Set<LocaleKey>(['ro', 'en', 'bg', 'el', 'hu', 'ru']);

const LOCALE_STORAGE_KEY = 'crm_locale';

// Dynamic loaders — webpack splits each locale into its own chunk.
// Each chunk is only fetched when that locale is first activated.
const localeLoaders: Record<LocaleKey, () => Promise<Record<string, unknown>>> = {
  ro: () => import('./locales/ro.json').then((m) => m.default as Record<string, unknown>),
  en: () => import('./locales/en.json').then((m) => m.default as Record<string, unknown>),
  bg: () => import('./locales/bg.json').then((m) => m.default as Record<string, unknown>),
  el: () => import('./locales/el.json').then((m) => m.default as Record<string, unknown>),
  hu: () => import('./locales/hu.json').then((m) => m.default as Record<string, unknown>),
  ru: () => import('./locales/ru.json').then((m) => m.default as Record<string, unknown>),
};

// Module-level cache: once a locale chunk is fetched it stays in memory for the
// lifetime of the page — subsequent locale switches are instant.
const localeCache = new Map<LocaleKey, Record<string, unknown>>();

async function loadLocale(locale: LocaleKey): Promise<Record<string, unknown>> {
  const cached = localeCache.get(locale);
  if (cached) return cached;
  const translations = await localeLoaders[locale]();
  localeCache.set(locale, translations);
  return translations;
}

/**
 * Maps tenant locale strings (e.g. "ro_RO") to our i18n locale keys.
 */
function mapTenantLocale(tenantLocale: string | undefined): LocaleKey {
  if (!tenantLocale) return 'en';
  const prefix = tenantLocale.split('_')[0].toLowerCase();
  if (SUPPORTED_LOCALES.has(prefix as LocaleKey)) return prefix as LocaleKey;
  return 'en';
}

/**
 * Detects the browser's preferred language and maps it to a supported locale key.
 * Falls back to 'en' if the browser language is not supported.
 */
function detectBrowserLocale(): LocaleKey {
  if (typeof navigator === 'undefined') return 'en';
  const languages = navigator.languages ?? [navigator.language];
  for (const lang of languages) {
    const prefix = lang.split('-')[0].toLowerCase();
    if (SUPPORTED_LOCALES.has(prefix as LocaleKey)) return prefix as LocaleKey;
  }
  return 'en';
}

/**
 * Resolves a dot-separated key path against a nested object.
 * Returns the string value if found, or the original key as fallback.
 */
function resolvePath(obj: Record<string, unknown>, key: string): string {
  const parts = key.split('.');
  let current: unknown = obj;
  for (const part of parts) {
    if (current === null || typeof current !== 'object') return key;
    current = (current as Record<string, unknown>)[part];
  }
  if (typeof current === 'string') return current;
  return key;
}

interface I18nCompatShim {
  language: LocaleKey;
  isInitialized: boolean;
}

type TInterpolationValues = Record<string, string | number | boolean>;
type TFallbackOrOptions = string | (TInterpolationValues & { defaultValue?: string });

interface I18nContextType {
  /**
   * Translate a key. Compatible with both signatures:
   *  - `t('key', 'fallback')` — local provider style
   *  - `t('key', { name: 'X', count: 1 })` — react-i18next interpolation
   *  - `t('key', 'fallback', { name: 'X' })` — both
   */
  t: (key: string, fallbackOrOpts?: TFallbackOrOptions, opts?: TInterpolationValues) => string;
  locale: LocaleKey;
  setLocale: (locale: LocaleKey) => void;
  /** Compat shim for legacy callsites that read `i18n.language` (react-i18next API). */
  i18n: I18nCompatShim;
}

const I18nContext = createContext<I18nContextType>({
  t: (key: string) => key,
  locale: 'en',
  setLocale: () => {},
  i18n: { language: 'en', isInitialized: false },
});

interface I18nProviderProps {
  children: ReactNode;
  /** Tenant locale from the auth context (e.g. "ro_RO", "bg_BG"). Used as initial default. */
  tenantLocale?: string;
}

export function I18nProvider({ children, tenantLocale }: I18nProviderProps) {
  const [locale, setLocaleState] = useState<LocaleKey>(() => {
    // Priority: tenant locale (user profile) > localStorage > browser language > 'en'
    if (tenantLocale) return mapTenantLocale(tenantLocale);
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem(LOCALE_STORAGE_KEY) as LocaleKey | null;
      if (stored && SUPPORTED_LOCALES.has(stored)) return stored;
    }
    return detectBrowserLocale();
  });

  // translations === null means the active locale chunk has not yet arrived.
  const [translations, setTranslations] = useState<Record<string, unknown> | null>(null);

  // Load the initial locale chunk on mount, and reload whenever locale changes.
  useEffect(() => {
    let cancelled = false;
    setTranslations(null); // clear while loading so stale keys never show
    loadLocale(locale).then((dict) => {
      if (!cancelled) setTranslations(dict);
    });
    return () => {
      cancelled = true;
    };
  }, [locale]);

  // When tenantLocale changes (user logs in / profile loads), apply the user's profile language
  // as the authoritative source. This prevents race conditions where a stale localStorage value
  // from a previous session differs from the user's actual language preference on the API.
  useEffect(() => {
    if (tenantLocale) {
      const mapped = mapTenantLocale(tenantLocale);
      setLocaleState(mapped);
      localStorage.setItem(LOCALE_STORAGE_KEY, mapped);
      // Also store in a cookie so server-side layout.tsx can read it
      document.cookie = `crm_locale=${mapped}; path=/; max-age=${60 * 60 * 24 * 365}`;
    } else if (!localStorage.getItem(LOCALE_STORAGE_KEY)) {
      // No profile language and no stored preference — fall back to browser language and persist it
      const browserLocale = detectBrowserLocale();
      setLocaleState(browserLocale);
      localStorage.setItem(LOCALE_STORAGE_KEY, browserLocale);
    }
  }, [tenantLocale]);

  const setLocale = useCallback((newLocale: LocaleKey) => {
    setLocaleState(newLocale);
    localStorage.setItem(LOCALE_STORAGE_KEY, newLocale);
    // Also store in a cookie so server-side layout.tsx can read it
    document.cookie = `crm_locale=${newLocale}; path=/; max-age=${60 * 60 * 24 * 365}`;
  }, []);

  const t = useCallback(
    (key: string, fallbackOrOpts?: TFallbackOrOptions, opts?: TInterpolationValues): string => {
      // If translations haven't loaded yet, fall through to fallback/key immediately.
      const dict = translations ?? {};
      const resolved = resolvePath(dict, key);

      // Decompose 2nd arg: string fallback, or options object with defaultValue + interpolation
      let fallback: string | undefined;
      let interpolation: TInterpolationValues | undefined;
      if (typeof fallbackOrOpts === 'string') {
        fallback = fallbackOrOpts;
        interpolation = opts;
      } else if (fallbackOrOpts && typeof fallbackOrOpts === 'object') {
        const { defaultValue, ...rest } = fallbackOrOpts;
        fallback = defaultValue;
        interpolation = rest as TInterpolationValues;
      }

      // Pick text: resolved-found OR fallback OR raw key
      let text = resolved !== key ? resolved : (fallback ?? key);

      // Interpolate {{name}} placeholders
      if (interpolation) {
        text = text.replace(/\{\{(\w+)\}\}/g, (_, name) =>
          name in interpolation! ? String(interpolation![name]) : `{{${name}}}`,
        );
      }
      return text;
    },
    [translations],
  );

  // Block render until the first locale chunk has loaded to prevent FOUC.
  // The chunk is served from webpack's local cache after the first visit,
  // so this pause is typically imperceptible (<50 ms on a warm load).
  if (!translations) return null;

  return (
    <I18nContext.Provider
      value={{ t, locale, setLocale, i18n: { language: locale, isInitialized: true } }}
    >
      {children}
    </I18nContext.Provider>
  );
}

/**
 * Returns the translation context.
 *
 * Optionally accepts a namespace prefix (react-i18next-style):
 *   useTranslation('admin') → t('users') resolves 'admin.users'
 * If no namespace given, t() resolves the full key path as-is.
 */
export function useTranslation(namespace?: string): I18nContextType {
  const ctx = useContext(I18nContext);
  if (!namespace) return ctx;
  // Wrap t() to prefix namespace, but only if key doesn't already contain a dot
  const t: I18nContextType['t'] = (key, fallbackOrOpts, opts) => {
    const fullKey = key.includes('.') ? key : `${namespace}.${key}`;
    return ctx.t(fullKey, fallbackOrOpts, opts);
  };
  return { ...ctx, t };
}
